import { useCallback, useEffect, useRef, useState } from "react";

export const DEFAULT_SESSION_VIEWPORT_WIDTH = 1440;
export const DEFAULT_SESSION_VIEWPORT_HEIGHT = 900;
export const MIN_SCALE = 0.25;
export const MAX_SCALE = 2;
export const SCALE_STEP = 0.1;

export function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

export function fitScaleForWidth(width: number): number {
  return clampScale(width > 0 ? width / DEFAULT_SESSION_VIEWPORT_WIDTH : 1);
}

export function stepScale(scale: number, direction: -1 | 1): number {
  return clampScale(Math.round((scale + direction * SCALE_STEP) * 100) / 100);
}

export function useBrowserSessionZoom() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [fitToWidth, setFitToWidth] = useState(true);
  const [manualScale, setManualScale] = useState(1);
  const fitScale = fitScaleForWidth(containerWidth);
  const scale = fitToWidth ? fitScale : manualScale;

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const measure = () => setContainerWidth(element.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const zoomIn = useCallback(() => {
    setManualScale(stepScale(scale, 1));
    setFitToWidth(false);
  }, [scale]);
  const zoomOut = useCallback(() => {
    setManualScale(stepScale(scale, -1));
    setFitToWidth(false);
  }, [scale]);
  const resetZoom = useCallback(() => setFitToWidth(true), []);

  return {
    containerRef, scale, fitToWidth, zoomIn, zoomOut, resetZoom,
    canZoomIn: scale < MAX_SCALE, canZoomOut: scale > MIN_SCALE,
  };
}
